import { abbreviate } from 'app/utils/numbers'
import { get } from 'lodash'
import pluralize from 'pluralize'
import { mapGetters } from 'vuex'
import accounting from 'accounting'

import {
  faArrowDownToLine,
  faBan,
  faBox,
  faCheck,
  faExclamationTriangle,
  faCloudArrowUp,
  faHandReceiving,
  faIcons,
  faChartSimple,
  faShapes,
  faSwatchbook,
  faSack,
  faSuitcase,
  faUser,
  faUsers,
  faUserGroup,
  faUserHeadset,
  faGlobe,
  faFileCertificate
} from 'app/icons/v7/solid'

import { faSparkles } from 'app/icons/v7/sharpSolid'
import { faDiamondHalfStroke } from 'app/icons/v7/sharpDuotoneSolid'

const perilAspects = ['kits', 'pageviews', 'uploads', 'autosubsetting', 'support', 'packages', 'bandwidth']

function removeIconColor(aspects) {
  const transformed = { ...aspects }

  Object.keys(transformed).forEach((aspect) => {
    transformed[aspect].iconColor = ''
  })

  return transformed
}

function inPeril(aspects) {
  const transformed = removeIconColor(aspects)

  perilAspects.forEach((aspect) => {
    transformed[aspect].icon = faExclamationTriangle
    transformed[aspect].iconColor = 'text-warning'
  })

  return transformed
}

function lost(aspects, { determinations }) {
  const transformed = removeIconColor(aspects)

  perilAspects.forEach((aspect) => {
    transformed[aspect].color = 'text-gray'
    transformed[aspect].icon = faBan
    transformed[aspect].iconColor = 'text-gray'
  })

  if (!determinations.exists && determinations.isBacker) {
    transformed.icons.label = 'All Version 5 Pro Icons'
    transformed.styles.label = "All Version 5's 4 Styles + Brands"
  }

  return transformed
}

export const ComponentContext = {
  props: {
    plan: {
      type: Object
    },

    subscription: {
      type: Object
    },

    determinations: {
      type: Object,
      required: true
    },

    colorSet: {
      type: Object,
      require: true
    }
  },

  computed: {
    ...mapGetters('licenses', ['currentLicenses', 'hasPreOrderLicense']),

    context() {
      const { indication, ended, isBacker, preOrderBackerStage, hasActivePaidSubscription } = this.determinations
      const plan = this.plan || get(this.subscription, ['plan'])
      const permits = this.$permits
      const free = this.determinations.isFree
      const color = this.colorSet
      const seatUpgradePrice = get(plan, ['seatUpgradePrice', 'amount'], 0) / 100
      const customIcons = isBacker && !hasActivePaidSubscription ? 250 : get(plan, ['customIcons'], 0)
      const pageviews = isBacker && !hasActivePaidSubscription ? 1000000 : get(plan, ['monthlyPageviews'], 0)
      const bandwidth = get(plan, ['monthlyBandwidth'], 0)
      const hasProPlus = get(plan, ['iconCollection', 'name']) === 'pro-plus-collection'
      const deniedIcon = ['fas', 'ban']
      const deniedIconColor = 'text-gray'
      const routeName = this.$route.name
      const isSignedInOnAccountPage = routeName === 'account.general' && this.$route.meta.isSignedIn
      const hasProBackerLicenseOnly = this.hasPreOrderLicense && this.currentLicenses.length === 1

      let val = {
        'lifetime5': {
          color: '',
          icon: faHandReceiving,
          iconColor: color.icon
        },
        'styles': {
          label: '',
          icon: faSwatchbook
        },
        'icons': {
          color: '',
          icon: faIcons,
          iconColor: color.icon,
          hoverColor: color.icon,
          hasProPlus,
          href:
            this.determinations.isBacker && !this.determinations.active
              ? this.$router.resolve(this.$search.v5({ free })).href
              : this.$router.resolve(this.$search({ free })).href,
          label: (() => {
            if (free) return `${this.$counts.icons.free.formatted} Free Icons`
            if (hasProPlus) return `All ${this.$counts.icons.all.formatted} Icons`
            return `${this.$counts.icons.proWithoutPlus.formatted} Pro Icons`
          })()
        },
        'classic': {
          color: '',
          icon: faSwatchbook,
          iconColor: color.icon,
          hoverColor: color.icon,
          href: this.$router.resolve(this.$search({ iconPacks: ['classic'] })).href,
          label: free ? '1 Classic Style' : `${this.getStyleCountByFamily('classic')} Classic ${pluralize('Style', this.getStyleCountByFamily('classic'))}`
        },
        'sharp': {
          color: '',
          icon: this.determinations.ended ? deniedIcon : faSparkles,
          iconColor: this.determinations.ended ? deniedIconColor : 'color.icon',
          hoverColor: color.icon,
          href: this.$router.resolve(this.$search({ iconPacks: ['sharp'] })).href,
          label: `${this.getStyleCountByFamily('sharp')} Sharp ${pluralize('Style', this.getStyleCountByFamily('sharp'))}`
        },
        'sharp-duotone': {
          color: '',
          icon: this.determinations.ended ? deniedIcon : faDiamondHalfStroke,
          iconColor: this.determinations.ended ? deniedIconColor : 'color.icon',
          hoverColor: color.icon,
          href: this.$router.resolve(this.$search({ iconPacks: ['sharp-duotone'] })).href,
          label: `${this.getStyleCountByFamily('sharp-duotone')} Sharp Duotone ${pluralize('Style', this.getStyleCountByFamily('sharp-duotone'))}`
        },
        'proPlus': {
          icon: faShapes,
          iconColor: color.icon,
          hoverColor: color.icon,
          href: this.$router.resolve(this.$search({ iconCollections: ['pro-plus'] })).href,
          label: 'Pro Plus Collection'
        },
        'brands': {
          color: '',
          icon: faSack,
          iconColor: color.icon,
          hoverColor: color.icon,
          href: this.$router.resolve(this.$search({ iconCollections: ['brands'] })).href,
          label: 'Brands Collection'
        },
        'seats': {
          color: '',
          icon: plan && get(plan, ['seats'], 0) === 1 ? faUser : plan && get(plan, ['seats'], 0) > 10 ? faUsers : faUserGroup,
          iconColor: color.icon,
          hoverColor: color.icon,
          extraSeatUpgradeInterval: get(plan, ['seatUpgradeInterval'], 1),
          extraSeatCostFormatted: seatUpgradePrice > 0 ? accounting.formatMoney(seatUpgradePrice, null, 0) : null
        },
        'kits': {
          color: '',
          icon: faSuitcase,
          iconColor: color.icon
        },
        'pageviews': {
          color: '',
          icon: faChartSimple,
          iconColor: color.icon,
          hoverColor: color.icon,
          label: `${abbreviate(pageviews)} Pageviews`
        },
        'uploads': {
          color: customIcons === 0 ? deniedIconColor : '',
          icon: customIcons === 0 ? deniedIcon : faCloudArrowUp,
          iconColor: customIcons === 0 ? deniedIconColor : color.icon,
          label: 'Icon Uploads'
        },
        'bandwidth': {
          color: bandwidth === 0 ? deniedIconColor : '',
          icon: bandwidth === 0 ? deniedIcon : faBox,
          iconColor: bandwidth === 0 ? deniedIconColor : color.icon,
          label: bandwidth === 0 ? `${bandwidth} bandwidth per month` : `${bandwidth} GB Bandwidth`
        },
        'autosubsetting': {
          hasAutoSubsetting: getAutoSubsettingAccess(),
          color: getAutoSubsettingAccess() ? '' : deniedIconColor,
          icon: getAutoSubsettingAccess() ? faCheck : deniedIcon,
          iconColor: getAutoSubsettingAccess() ? '' : deniedIconColor
        },
        'support': {
          hasAccessToHumanSupport: getServiceOfferedOrPermitted('supportEnabled', 'humanSupport'),
          color: getServiceOfferedOrPermitted('supportEnabled', 'humanSupport') ? '' : deniedIconColor,
          icon: getServiceOfferedOrPermitted('supportEnabled', 'humanSupport') ? faUserHeadset : deniedIcon,
          iconColor: getServiceOfferedOrPermitted('supportEnabled', 'humanSupport') ? '' : deniedIconColor
        },
        'download': {
          hasAccessToProDownloads: getServiceOfferedOrPermitted('proDownloadEnabled', 'accessToProDownload'),
          color: '',
          icon: getServiceOfferedOrPermitted('proDownloadEnabled', 'accessToProDownload') ? faArrowDownToLine : faGlobe,
          iconColor: color.icon
        },
        'license': {
          hasPerpetualLicense: getPerpetualLicenseAccess(),
          hasProBackerLicenseOnly,
          color: getPerpetualLicenseAccess() ? '' : deniedIconColor,
          icon: faFileCertificate,
          iconColor: color.icon
        },
        'packages': {
          hasAccessToPrivateNpm: getServiceOfferedOrPermitted('privateRegistriesEnabled', 'privateNpm'),
          color: getServiceOfferedOrPermitted('privateRegistriesEnabled', 'privateNpm') ? '' : deniedIconColor,
          icon: getServiceOfferedOrPermitted('privateRegistriesEnabled', 'privateNpm') ? faBox : deniedIcon,
          iconColor: getServiceOfferedOrPermitted('privateRegistriesEnabled', 'privateNpm') ? '' : deniedIconColor
        }
      }

      function getAutoSubsettingAccess() {
        if (routeName.includes('plans')) return plan.supportsAutoSubsetting
        if (isSignedInOnAccountPage) return permits.has('accessToKits') && plan.supportsAutoSubsetting

        return false
      }

      function getPerpetualLicenseAccess() {
        return get(plan, ['perpetualLicenseEnabled']) || hasProBackerLicenseOnly
      }

      function getServiceOfferedOrPermitted(planService, permitService) {
        if (routeName.includes('plans')) return get(plan, [planService])
        if (isSignedInOnAccountPage) return permits.has(permitService)

        return false
      }

      if (!this.plan && (indication === 'warning' || indication === 'danger' || ['alerting', 'grace-period'].indexOf(preOrderBackerStage) > -1)) {
        val = inPeril(val, { determinations: this.determinations })
      }

      if (!this.plan && (ended || preOrderBackerStage === 'shutoff')) {
        val = lost(val, { determinations: this.determinations })
      }

      return val
    }
  },

  methods: {
    getStyleCountByFamily(family) {
      /**
        $releases.active.familyStyleCounts[family] has duotone-solid as it own style in the Duotone family, but for
        this style count we want to count duotone solid as part of the Classic family

        $releases.active.familyStyleCounts[family] also has Brands as a style in the Classic family, but for this
        style count we do NOT want to count it as part of the Classic family

        Being these two items offset, duotone solid is NOT part of the Classic family and Brands is part of the Classic family,
        the $releases.active.familyStyleCounts[family] count is 5 and correct.
      */
      return Object.keys(this.$releases.active.familyStyleCounts[family]).length
    }
  }
}
